import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  History, Loader2, ChevronDown, ChevronRight, Trash2, RefreshCw,
  Mail, Paperclip, FlaskConical, Send, Eye, Code2, Reply, BellRing, Pencil,
  PauseCircle, Sparkles, AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CampaignProgressPanel from "@/components/CampaignProgressPanel";

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  contacts_review: "bg-blue-100 text-blue-700 border-blue-200",
  drafting: "bg-violet-100 text-violet-700 border-violet-200",
  pending_sender_verification: "bg-amber-100 text-amber-700 border-amber-200",
  sending: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  simulated: "bg-slate-200 text-slate-700 border-slate-300",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
};

const SEND_BADGE = {
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  queued: "bg-blue-100 text-blue-700 border-blue-200",
  simulated: "bg-slate-200 text-slate-700 border-slate-300",
  skipped: "bg-amber-100 text-amber-700 border-amber-200",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
  pending: "bg-slate-100 text-slate-500 border-slate-200",
};

// Live SendGrid delivery outcome per recipient (updated by the Event Webhook).
const DELIVERY_BADGE = {
  delivered: { cls: "bg-green-100 text-green-800 border-green-300", label: "Delivered" },
  bounced: { cls: "bg-rose-100 text-rose-700 border-rose-300", label: "Bounced" },
  spam_reported: { cls: "bg-rose-100 text-rose-700 border-rose-300", label: "Spam report" },
  unsubscribed: { cls: "bg-slate-200 text-slate-700 border-slate-300", label: "Unsubscribed" },
  blocked: { cls: "bg-orange-100 text-orange-800 border-orange-300", label: "Blocked" },
  dropped: { cls: "bg-rose-100 text-rose-700 border-rose-300", label: "Dropped" },
  deferred: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Deferred" },
};

const fmtDate = (s) => s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

// Campaigns that haven't been sent yet can be reopened in the wizard to keep editing.
const RESUMABLE_STATUSES = ["draft", "contacts_review", "drafting", "pending_sender_verification", "scheduled"];

export default function BespokeCampaignHistory({ onResume }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [viewing, setViewing] = useState({});      // recipient_id -> show email body
  const [fixSubject, setFixSubject] = useState(""); // campaign-wide subject for resend
  const [resending, setResending] = useState(false);

  const applyDetail = (d) => { setDetail(d); setFixSubject(d?.campaign?.master_subject || ""); };
  const toggleView = (rid) => setViewing((v) => ({ ...v, [rid]: !v[rid] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/campaigns/bespoke`, { credentials: "include" });
      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json()).detail || ""; } catch { /* non-json */ }
        throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ""}`);
      }
      const d = await res.json();
      setCampaigns(d.campaigns || []);
    } catch (e) {
      toast.error(`Failed to load campaigns (${e.message || "network error"})`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id); setDetail(null); setViewing({}); setDetailLoading(true);
    try {
      const d = await fetch(`${API}/campaigns/bespoke/${id}`, { credentials: "include" }).then((r) => r.json());
      applyDetail(d);
      loadFollowups(id);
    } catch { toast.error("Failed to load detail"); }
    finally { setDetailLoading(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this campaign? This removes it from history (recipients too).")) return;
    try {
      await fetch(`${API}/campaigns/bespoke/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Campaign deleted");
      if (expanded === id) { setExpanded(null); setDetail(null); }
      load();
    } catch { toast.error("Failed to delete"); }
  };

  // ── Send guardrail: EVERY send (flush/resend) needs a FRESH 6-digit code ──
  const [flushing, setFlushing] = useState(null);
  const [pendingSend, setPendingSend] = useState(null); // { id, kind: "flush"|"resend" }
  const [verifyCode, setVerifyCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  // Step 1 — email the sender a fresh code, then open the code dialog.
  const requestSendCode = async (id, kind, e) => {
    if (e) e.stopPropagation();
    setCodeBusy(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${id}/approve`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not start verification"); return; }
      if (d.test_mode && d.code) toast.success(`Test mode: code ${d.code} (no email sent)`);
      else toast.success(`Verification code sent to ${d.sender_email}`);
      setVerifyCode("");
      setPendingSend({ id, kind });
    } catch { toast.error("Could not start verification"); }
    finally { setCodeBusy(false); }
  };

  const doFlush = async (id, code) => {
    setFlushing(id);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${id}/flush`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Send failed"); return false; }
      const { sent = 0, failed = 0 } = d.outbox || {};
      const before = d.before || {};
      if (failed > 0) {
        const err = (d.prior_errors && d.prior_errors[0] && d.prior_errors[0].error) || "expand to see the error";
        toast.error(`${sent} sent, ${failed} failed — ${err}`);
      } else if (sent > 0) {
        toast.success(`Sent ${sent} email(s)`);
      } else if (before.sent > 0 && !before.queued && !before.failed) {
        toast.message("Already accepted by the mail server earlier — if it didn't arrive, check the recipient's spam/junk or for a bounce.");
      } else if (d.prior_errors && d.prior_errors.length) {
        toast.error(`Previously failed: ${d.prior_errors[0].error}`);
      } else {
        toast.message("Nothing to send for this campaign right now.");
      }
      if (expanded === id && d.recipients) applyDetail({ campaign: d.campaign, recipients: d.recipients });
      load();
      return true;
    } catch { toast.error("Failed to send"); return false; }
    finally { setFlushing(null); }
  };

  // ── Phase B/C: reply tracking + follow-up nudges ──
  const [followups, setFollowups] = useState({}); // campaign_id -> {eligible_count, eligible:[], after_days, max_nudges}
  const [fuBusy, setFuBusy] = useState(null);
  const [replying, setReplying] = useState(null);

  const loadFollowups = useCallback(async (id) => {
    try {
      const d = await fetch(`${API}/campaigns/bespoke/${id}/followups`, { credentials: "include" }).then((r) => r.json());
      setFollowups((m) => ({ ...m, [id]: d }));
    } catch { /* non-blocking */ }
  }, []);

  const markReplied = async (id, recipientId) => {
    setReplying(recipientId);
    try {
      const d = await fetch(`${API}/campaigns/bespoke/${id}/recipients/${recipientId}/mark-replied`, {
        method: "POST", credentials: "include",
      }).then((r) => r.json());
      if (d.ok) {
        toast.success(`Marked replied${d.advanced_stage ? " — client advanced to Qualified" : ""}`);
        if (d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
        loadFollowups(id);
      } else { toast.error(d.detail || "Could not mark replied"); }
    } catch { toast.error("Failed to mark replied"); }
    finally { setReplying(null); }
  };

  const draftFollowups = async (id) => {
    setFuBusy(id);
    try {
      const d = await fetch(`${API}/campaigns/bespoke/${id}/followups/draft`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}",
      }).then((r) => r.json());
      toast.success(`Drafted ${d.drafted || 0} nudge(s)${d.flagged ? ` · ${d.flagged} flagged` : ""}`);
      if (d.followups) setFollowups((m) => ({ ...m, [id]: d.followups }));
    } catch { toast.error("Draft failed"); }
    finally { setFuBusy(null); }
  };

  const sendFollowups = async (id) => {
    if (!window.confirm("Send the drafted follow-up nudges now?")) return;
    setFuBusy(id);
    try {
      const d = await fetch(`${API}/campaigns/bespoke/${id}/followups/send`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}",
      }).then((r) => r.json());
      if (d.detail) { toast.error(d.detail); return; }
      toast.success(`Sent ${d.sent || 0} follow-up(s)`);
      if (d.followups) setFollowups((m) => ({ ...m, [id]: d.followups }));
      toggle(id); toggle(id);
    } catch { toast.error("Send failed"); }
    finally { setFuBusy(null); }
  };

  const doResend = async (id, code) => {
    setResending(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${id}/resend`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, subject: fixSubject || undefined, only_unsent: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Resend failed"); return false; }
      const { sent = 0, failed = 0 } = d.outbox || {};
      if (failed > 0) {
        const err = (d.errors && d.errors[0] && d.errors[0].error) || "see error below";
        toast.error(`Resent ${d.resent || 0}, ${failed} failed — ${err}`);
      } else if (sent > 0) {
        toast.success(`Resent ${sent} email(s)`);
      } else {
        toast.message(`Re-queued ${d.resent || 0} — they'll send shortly`);
      }
      if (expanded === id && d.recipients) applyDetail({ campaign: d.campaign, recipients: d.recipients });
      load();
      return true;
    } catch { toast.error("Resend failed"); return false; }
    finally { setResending(false); }
  };

  const [issueBusy, setIssueBusy] = useState(null);
  const excludeRecipient = async (id, rid) => {
    if (!window.confirm("Exclude this recipient from the campaign? They won't be sent to again.")) return;
    setIssueBusy(rid);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${id}/recipients/${rid}/exclude`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Couldn't exclude"); return; }
      toast.success("Recipient excluded");
      if (d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
    } catch { toast.error("Couldn't exclude"); } finally { setIssueBusy(null); }
  };
  const retryBounce = async (id, rid) => {
    if (!window.confirm("Retry this recipient? Only do this if the bounce was temporary or the address was fixed — repeated hard bounces hurt deliverability.")) return;
    setIssueBusy(rid);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${id}/recipients/${rid}/retry-bounce`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Couldn't retry"); return; }
      toast.success("Re-enabled — use 'Fix & resend' below to send again");
      if (d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
    } catch { toast.error("Couldn't retry"); } finally { setIssueBusy(null); }
  };


  // Step 2 — user entered the fresh code; run the queued send action.
  const confirmSend = async () => {
    if (!pendingSend || verifyCode.length !== 6) return;
    const { id, kind } = pendingSend;
    setCodeBusy(true);
    const ok = kind === "flush" ? await doFlush(id, verifyCode) : await doResend(id, verifyCode);
    setCodeBusy(false);
    if (ok) { setPendingSend(null); setVerifyCode(""); }
  };

  // ── Out-of-office: paste an OOO for Rex to classify, or manually hold/resume ──
  const [oooTarget, setOooTarget] = useState(null); // { campaignId, recipientId, name, email }
  const [oooText, setOooText] = useState("");
  const [oooUntil, setOooUntil] = useState("");
  const [oooBusy, setOooBusy] = useState(false);
  const isHeld = (ct) => ct.hold_until && new Date(ct.hold_until) > new Date();
  const openOOO = (campaignId, ct) => { setOooTarget({ campaignId, recipientId: ct.recipient_id, name: ct.name, email: ct.email }); setOooText(""); setOooUntil(""); };

  const submitHold = async () => {
    if (!oooTarget || !oooUntil) return;
    setOooBusy(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${oooTarget.campaignId}/recipients/${oooTarget.recipientId}/hold`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ until: oooUntil }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Failed to hold"); return; }
      toast.success(`Held until ${oooUntil}`);
      if (expanded === oooTarget.campaignId && d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
      setOooTarget(null); load();
    } catch { toast.error("Failed to hold"); } finally { setOooBusy(false); }
  };

  const submitIngest = async () => {
    if (!oooTarget || oooText.trim().length < 10) return;
    setOooBusy(true);
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${oooTarget.campaignId}/recipients/${oooTarget.recipientId}/ingest-ooo`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: oooText }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Failed to ingest"); return; }
      if (!d.is_ooo) { toast.message(d.message || "Not detected as an out-of-office message"); return; }
      const msg = (d.classification === "delegate" && d.delegate_created)
        ? `OOO → warm referral drafted to ${d.delegate_name || d.delegate_email} (review & approve to send)`
        : d.classification === "team"
        ? "OOO → held; it names colleagues generally — add a contact to pursue"
        : `OOO → held until ${d.return_date || "their return"}`;
      toast.success(msg);
      if (expanded === oooTarget.campaignId && d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
      setOooTarget(null); load();
    } catch { toast.error("Failed to ingest"); } finally { setOooBusy(false); }
  };

  const resumeRecipient = async (campaignId, recipientId) => {
    try {
      const r = await fetch(`${API}/campaigns/bespoke/${campaignId}/recipients/${recipientId}/hold`, { method: "DELETE", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Failed"); return; }
      toast.success("Resumed — eligible for follow-ups again");
      if (expanded === campaignId && d.recipients) applyDetail({ campaign: detail.campaign, recipients: d.recipients });
      load();
    } catch { toast.error("Failed to resume"); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div data-testid="bespoke-history" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-500 flex items-center gap-2">
          <History className="w-4 h-4" /> {campaigns.length} campaign(s)
        </h3>
        <Button size="sm" variant="outline" onClick={load} data-testid="history-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      {campaigns.length === 0 && (
        <Card><CardContent className="py-12 text-center text-slate-400 text-sm">No bespoke campaigns yet. Create one from the "Bespoke outreach" tab.</CardContent></Card>
      )}

      {campaigns.map((c) => {
        const st = c.stats || { total: 0 };
        const isOpen = expanded === c.campaign_id;
        return (
          <Card key={c.campaign_id} data-testid={`history-row-${c.campaign_id}`}>
            <CardContent className="p-0">
              <button onClick={() => toggle(c.campaign_id)} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 text-left">
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 truncate">{c.name}</span>
                    {c.test_mode && <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 text-xs"><FlaskConical className="w-3 h-3 mr-1" />Test</Badge>}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {c.target_type} · {c.client_ids?.length || 0} client(s) · as {c.sender_name || c.sender_email} · {fmtDate(c.sent_at || c.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Fixed Progress column — present for every campaign, any status */}
                  <div className="hidden sm:flex flex-col items-end w-24" data-testid={`history-progress-${c.campaign_id}`}
                    title={`${c.progress_pct ?? 0}% delivered / complete`}>
                    <span className="text-[11px] font-semibold text-slate-600">{c.progress_pct ?? 0}%</span>
                    <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-indigo-600 transition-all" style={{ width: `${c.progress_pct ?? 0}%` }} />
                    </div>
                  </div>
                  {st.sent > 0 && <Badge variant="outline" className={SEND_BADGE.sent}>{st.sent} sent</Badge>}
                  {st.queued > 0 && <Badge variant="outline" className={SEND_BADGE.queued}>{st.queued} queued</Badge>}
                  {st.simulated > 0 && <Badge variant="outline" className={SEND_BADGE.simulated}>{st.simulated} simulated</Badge>}
                  {st.failed > 0 && <Badge variant="outline" className={SEND_BADGE.failed}>{st.failed} failed</Badge>}
                  <Badge variant="outline" className={STATUS_BADGE[c.status] || STATUS_BADGE.draft}>{(c.status || "").replace(/_/g, " ")}</Badge>
                  {onResume && RESUMABLE_STATUSES.includes(c.status) && (
                    <span onClick={(e) => { e.stopPropagation(); onResume(c.campaign_id); }} data-testid={`history-resume-${c.campaign_id}`}
                      className="flex items-center gap-1 text-xs text-white bg-indigo-600 hover:bg-indigo-700 px-2 py-1 rounded cursor-pointer">
                      <Pencil className="w-3.5 h-3.5" /> Resume editing
                    </span>
                  )}
                  {!c.test_mode && (st.queued > 0 || st.failed > 0) && (
                    <span onClick={(e) => requestSendCode(c.campaign_id, "flush", e)} data-testid={`history-send-now-${c.campaign_id}`}
                      className="flex items-center gap-1 text-xs text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded cursor-pointer">
                      {flushing === c.campaign_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {st.failed > 0 ? "Retry now" : "Send now"}
                    </span>
                  )}
                  <span onClick={(e) => remove(c.campaign_id, e)} data-testid={`history-delete-${c.campaign_id}`}
                    className="p-1.5 rounded hover:bg-rose-50 cursor-pointer"><Trash2 className="w-4 h-4 text-rose-400" /></span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t bg-slate-50/50 p-4" data-testid={`history-detail-${c.campaign_id}`}>
                  {detailLoading || !detail ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg border p-3" data-testid={`progress-section-${c.campaign_id}`}>
                        <CampaignProgressPanel campaignId={c.campaign_id} />
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-4">
                        <span><Mail className="w-3 h-3 inline mr-1" />Reply-To: {detail.campaign.reply_to_email}</span>
                        <span><Paperclip className="w-3 h-3 inline mr-1" />{(detail.campaign.attachments || []).length} attachment(s)</span>
                        {detail.campaign.send_mode === "compose"
                          ? <span>Subject: <em className="text-slate-500">bespoke per recipient</em></span>
                          : <span>Subject: {detail.campaign.master_subject || <em className="text-rose-500">none</em>}</span>}
                      </div>

                      {(() => {
                        const contacts = (detail.recipients || []).flatMap((g) => g.contacts).filter((x) => x.decision === "include");
                        const failed = contacts.filter((x) => x.send_status === "failed");
                        const noSubject = contacts.filter((x) => !["sent", "excluded"].includes(x.send_status)
                          && !(x.bespoke_subject || "").trim() && !(detail.campaign.master_subject || "").trim());
                        if (detail.campaign.test_mode || (failed.length === 0 && noSubject.length === 0)) return null;
                        const msg = noSubject.length > 0
                          ? `${noSubject.length} email(s) have no subject line. Add one, then resend:`
                          : `${failed.length} email(s) failed to send. Resend:`;
                        return (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid={`resend-bar-${c.campaign_id}`}>
                            <p className="text-xs text-amber-800 font-medium">{msg}</p>
                            <div className="flex gap-2">
                              <Input value={fixSubject} onChange={(e) => setFixSubject(e.target.value)} placeholder="Subject line"
                                className="flex-1 text-xs h-8" data-testid={`resend-subject-${c.campaign_id}`} />
                              <Button size="sm" disabled={resending || codeBusy} onClick={() => requestSendCode(c.campaign_id, "resend")}
                                data-testid={`resend-btn-${c.campaign_id}`} className="bg-emerald-600 hover:bg-emerald-700">
                                {resending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Fix &amp; resend
                              </Button>
                            </div>
                          </div>
                        );
                      })()}

                      {(() => {
                        const fu = followups[c.campaign_id];
                        if (!fu) return null;
                        const hasDrafts = (fu.eligible || []).some((e) => e.followup_status === "drafted");
                        return (
                          <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-3" data-testid={`followups-panel-${c.campaign_id}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                                <BellRing className="w-3.5 h-3.5" />
                                Follow-ups · {fu.eligible_count} awaiting reply &gt; {fu.after_days}d (cap {fu.max_nudges})
                              </p>
                              {fu.eligible_count > 0 && (
                                <div className="flex items-center gap-2">
                                  <Button size="sm" variant="outline" onClick={() => draftFollowups(c.campaign_id)} disabled={fuBusy === c.campaign_id}
                                    className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100" data-testid={`draft-followups-${c.campaign_id}`}>
                                    {fuBusy === c.campaign_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Draft nudges
                                  </Button>
                                  {hasDrafts && (
                                    <Button size="sm" onClick={() => sendFollowups(c.campaign_id)} disabled={fuBusy === c.campaign_id}
                                      className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid={`send-followups-${c.campaign_id}`}>
                                      <Send className="w-3.5 h-3.5" /> Approve &amp; send
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            {hasDrafts && (
                              <div className="mt-2 space-y-1.5">
                                {(fu.eligible || []).filter((e) => e.followup_status === "drafted").map((e) => (
                                  <div key={e.recipient_id} className="text-xs bg-white border border-amber-100 rounded p-2" data-testid={`followup-draft-${e.recipient_id}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{e.name}</span>
                                      <span className="text-slate-400">·</span>
                                      <span className="text-slate-600 truncate">{e.followup_subject}</span>
                                      {(e.followup_flags || []).length > 0 && (
                                        <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-[10px]">{e.followup_flags.length} style flag</Badge>
                                      )}
                                    </div>
                                    <div className="mt-1 text-slate-600 prose prose-sm max-w-none"
                                      dangerouslySetInnerHTML={{ __html: (e.followup_html || "").replace(/\{\{sender_name\}\}/g, detail.campaign.sender_name || "") }} />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {(() => {
                        const issues = (detail.recipients || []).flatMap((g) => g.contacts)
                          .filter((x) => ["bounced", "spam_reported", "unsubscribed", "blocked", "dropped"].includes(x.delivery_status));
                        if (issues.length === 0) return null;
                        return (
                          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2" data-testid={`delivery-issues-${c.campaign_id}`}>
                            <p className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5" /> {issues.length} delivery issue(s) — these did NOT reach the recipient
                            </p>
                            <div className="space-y-1.5">
                              {issues.map((x) => (
                                <div key={x.recipient_id} className="flex items-center gap-2 flex-wrap text-xs bg-white border border-rose-100 rounded p-2" data-testid={`delivery-issue-${x.recipient_id}`}>
                                  <span className="font-medium">{x.name || x.email}</span>
                                  <span className="text-slate-500 truncate">{x.email}</span>
                                  <Badge variant="outline" className={`${(DELIVERY_BADGE[x.delivery_status] || {}).cls || ""} text-xs`}>{(DELIVERY_BADGE[x.delivery_status] || {}).label || x.delivery_status}</Badge>
                                  {x.delivery_reason && <span className="text-rose-600">— {x.delivery_reason}</span>}
                                  <div className="ml-auto flex items-center gap-2">
                                    <button onClick={() => retryBounce(c.campaign_id, x.recipient_id)} disabled={issueBusy === x.recipient_id}
                                      className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50" data-testid={`retry-bounce-${x.recipient_id}`}>
                                      {issueBusy === x.recipient_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Retry"}
                                    </button>
                                    <button onClick={() => excludeRecipient(c.campaign_id, x.recipient_id)} disabled={issueBusy === x.recipient_id}
                                      className="text-rose-600 hover:text-rose-800 disabled:opacity-50" data-testid={`exclude-${x.recipient_id}`}>
                                      Exclude
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {(detail.recipients || []).map((g) => (
                        <div key={g.client_id} className="bg-white rounded-lg border p-3">
                          <p className="font-medium text-sm text-slate-700 mb-2">{g.company_name}</p>
                          <div className="space-y-1.5">
                            {g.contacts.filter((x) => x.decision === "include").map((ct) => (
                              <div key={ct.recipient_id} className="text-xs border-b last:border-0 pb-1.5 last:pb-0">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium w-36 truncate">{ct.name || "—"}</span>
                                  <span className="text-slate-500 flex-1 truncate">{ct.email}</span>
                                  <Badge variant="outline" className="capitalize text-xs">{ct.verify_status}</Badge>
                                  {ct.approval_status === "approved"
                                    ? <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200 text-xs">approved</Badge>
                                    : <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 text-xs">needs review</Badge>}
                                  <Badge variant="outline" className={`${SEND_BADGE[ct.send_status] || SEND_BADGE.pending} text-xs`}>{ct.send_status}{ct.wave_index != null ? ` · wave ${ct.wave_index + 1}` : ""}</Badge>
                                  {ct.reply_status === "replied" && (
                                    <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-xs" data-testid={`reply-badge-${ct.recipient_id}`}>replied</Badge>
                                  )}
                                  {ct.delivery_status && DELIVERY_BADGE[ct.delivery_status] && (
                                    <Badge variant="outline" className={`${DELIVERY_BADGE[ct.delivery_status].cls} text-xs`}
                                      title={ct.delivery_reason ? `Reason: ${ct.delivery_reason}` : undefined}
                                      data-testid={`delivery-badge-${ct.recipient_id}`}>
                                      {DELIVERY_BADGE[ct.delivery_status].label}{ct.delivery_reason ? " ⓘ" : ""}
                                    </Badge>
                                  )}
                                  {ct.opened && (
                                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs" data-testid={`opened-badge-${ct.recipient_id}`}>Opened{ct.open_count > 1 ? ` ×${ct.open_count}` : ""}</Badge>
                                  )}
                                  {ct.clicked && (
                                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs" data-testid={`clicked-badge-${ct.recipient_id}`}>Clicked{ct.click_count > 1 ? ` ×${ct.click_count}` : ""}</Badge>
                                  )}
                                  {ct.send_status === "sent" && ct.reply_status !== "replied" && !ct.do_not_contact && (
                                    <button onClick={() => markReplied(c.campaign_id, ct.recipient_id)} disabled={replying === ct.recipient_id}
                                      data-testid={`mark-replied-${ct.recipient_id}`}
                                      className="flex items-center gap-1 text-teal-600 hover:text-teal-800 disabled:opacity-50">
                                      {replying === ct.recipient_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Reply className="w-3.5 h-3.5" />}Mark replied
                                    </button>
                                  )}
                                  {ct.send_status === "sent" && !isHeld(ct) && (
                                    <button onClick={() => openOOO(c.campaign_id, ct)} data-testid={`ooo-btn-${ct.recipient_id}`}
                                      className="flex items-center gap-1 text-amber-600 hover:text-amber-800">
                                      <PauseCircle className="w-3.5 h-3.5" />Out of office
                                    </button>
                                  )}
                                  {isHeld(ct) && (
                                    <span className="flex items-center gap-1.5" data-testid={`held-badge-${ct.recipient_id}`}>
                                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">On hold · back {new Date(ct.hold_until).toLocaleDateString()}</Badge>
                                      <button onClick={() => resumeRecipient(c.campaign_id, ct.recipient_id)} data-testid={`resume-btn-${ct.recipient_id}`} className="text-xs text-indigo-600 hover:text-indigo-800">Resume</button>
                                    </span>
                                  )}
                                  {ct.source === "ooo_delegate" && (
                                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs" data-testid={`delegate-badge-${ct.recipient_id}`}><Sparkles className="w-3 h-3 mr-1" />Warm referral (OOO)</Badge>
                                  )}
                                  {(ct.bespoke_html || "").trim() && (
                                    <button onClick={() => toggleView(ct.recipient_id)} data-testid={`view-email-${ct.recipient_id}`}
                                      className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800">
                                      <Eye className="w-3.5 h-3.5" />{viewing[ct.recipient_id] ? "Hide" : "View email"}
                                    </button>
                                  )}
                                </div>
                                {ct.send_status === "failed" && ct.error && (
                                  <p className="text-rose-600 mt-0.5 ml-[9.5rem]" data-testid={`send-error-${ct.recipient_id}`}>⚠ {ct.error}</p>
                                )}
                                {viewing[ct.recipient_id] && (
                                  <div className="mt-2 border rounded-md bg-slate-50 overflow-hidden" data-testid={`email-preview-${ct.recipient_id}`}>
                                    <div className="px-3 py-1.5 border-b bg-white text-xs">
                                      <span className="text-slate-400">Subject: </span>
                                      {(ct.bespoke_subject || "").trim() ? ct.bespoke_subject : ((detail.campaign.master_subject || "").trim() ? detail.campaign.master_subject : <em className="text-rose-500">none — add one above and resend</em>)}
                                    </div>
                                    <div className="px-3 py-2 text-sm prose prose-sm max-w-none bg-white"
                                      dangerouslySetInnerHTML={{ __html: (ct.bespoke_html || "").replace(/\{\{sender_name\}\}/g, detail.campaign.sender_name || "").replace(/\{\{sender_title\}\}/g, "") }} />
                                  </div>
                                )}
                              </div>
                            ))}
                            {g.contacts.filter((x) => x.decision === "include").length === 0 && (
                              <p className="text-xs text-slate-400">No included recipients.</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!pendingSend} onOpenChange={(o) => { if (!o) { setPendingSend(null); setVerifyCode(""); } }}>
        <DialogContent className="max-w-sm" data-testid="send-verify-dialog">
          <DialogHeader><DialogTitle>Enter verification code</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A fresh 6-digit code was emailed to the sender. Enter it to authorise this send — every send requires a new code.</p>
          <Input
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit code"
            className="text-center text-2xl tracking-widest font-mono mt-2"
            maxLength={6}
            data-testid="send-verify-code-input"
          />
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => { setPendingSend(null); setVerifyCode(""); }} data-testid="send-verify-cancel">Cancel</Button>
            <Button disabled={verifyCode.length !== 6 || codeBusy} onClick={confirmSend} data-testid="send-verify-confirm" className="bg-emerald-600 hover:bg-emerald-700">
              {codeBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Verify &amp; send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!oooTarget} onOpenChange={(o) => { if (!o) setOooTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="ooo-dialog">
          <DialogHeader><DialogTitle>Out of office — {oooTarget?.name || oooTarget?.email}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Paste their auto-reply and Rex will decide: <strong>hold</strong> until they're back, or — if it names a stand-in (PA/colleague) — draft a <strong>warm hand-off</strong> for you to review.</p>
          <Textarea
            value={oooText}
            onChange={(e) => setOooText(e.target.value)}
            placeholder="Paste the out-of-office email here…"
            className="mt-2 h-32 text-sm"
            data-testid="ooo-paste-input"
          />
          <Button disabled={oooBusy || oooText.trim().length < 10} onClick={submitIngest} data-testid="ooo-ingest-btn" className="bg-indigo-600 hover:bg-indigo-700 w-full">
            {oooBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Let Rex handle it
          </Button>
          <div className="flex items-center gap-2 my-1"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or just hold until</span><div className="h-px bg-slate-200 flex-1" /></div>
          <div className="flex gap-2">
            <Input type="date" value={oooUntil} onChange={(e) => setOooUntil(e.target.value)} className="flex-1 text-sm" data-testid="ooo-until-input" />
            <Button variant="outline" disabled={oooBusy || !oooUntil} onClick={submitHold} data-testid="ooo-hold-btn"><PauseCircle className="w-3.5 h-3.5 mr-1" />Hold</Button>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOooTarget(null)} data-testid="ooo-cancel">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
